// THE PRICE AS A CHOICE AND A NUMBER (2026-09-11). Reckless Heart Tattoo (Greenville SC), from its front desk: "Can
// they have three options to select from; 1. Decided upon fee, 2. Estimate range, 3. Hourly rate ($180/hour)?" Chris,
// the same afternoon: "perhaps the 'default' mode for a shop that opts in to the price is the dropdown + input (or in
// the settings, freeform is allowed)". South Carolina's rule is why it matters there: the charges, "whether a flat fee
// or hourly rate, shall be stated in writing, and the client shall be made aware of such charges and procedures as
// verified by his or her signature, prior to the procedure" (S.C. Code Regs. 60-111 § 1003).
//
// THE SHOP'S HOURLY RATE IS A DEFAULT, NEVER A FIXED PRICE. The first cut named it in the choice ("Hourly rate
// ($180/hour)") and printed it whatever the client typed, and Chris, reading the note that described it: "i think
// that's taking the hourly rate too literally. i figured it would perhaps default to 180/hr but allow it to be
// edited." · "and then it'll pre-fill the text input that can be changed?" So the choice says "Hourly rate", picking
// it fills the shop's rate into the rate box (prefillRate), and the box is the client's: what is in it when they sign
// is what the waiver says.
//
// WHAT IS POSTED STAYS ONE STRING, `price`, because the signed waiver, the shop app and the export all read that one
// field and have since TW-0154. This composes it from the choice and the numbers, in the words of the language the
// client is reading: "$450 (decided upon fee)", "$300 to $500 (estimate range)", "$180/hour (hourly rate)". A shop that
// chose "in their own words" in Settings keeps the one box it always had, and nothing here runs for it.

export type PriceStyle = "choices" | "free";
export type PriceKind = "" | "fixed" | "range" | "hourly";
export type PriceParts = { amount: string; low: string; high: string; rate: string };
export const NO_PRICE_PARTS: PriceParts = { amount: "", low: "", high: "", rate: "" };
/** TW-0275 — the longest each of the four number boxes takes. What they compose (composePrice) is the one `price` the form
 *  posts, and the API's signing door holds `price` to its own length (fieldLimits.ts, 60): the longest these can compose,
 *  a range of two of them, is 47 characters in English and 46 in Spanish, and price.test.ts keeps it under the door's. */
export const PRICE_PART_MAX = 12;

/** The words a composed price is made of, from the string table (i18n.ts) so both languages stay one table. */
export type PriceWords = {
  priceKindFixed: string;
  priceKindRange: string;
  priceKindHourly: string;
  priceRangeJoin: string;
  pricePerHour: string;
};

/** The shop's setting (`public_data.price_style`). Anything but "free" is the choices, which is the default, so a
 *  shop that turned the price on before the choices existed gets them too. `unknown` in, because the shop's public
 *  config is whatever the row said. */
export function priceStyleOf(publicData: unknown): PriceStyle {
  return (publicData as { price_style?: unknown } | null | undefined)?.price_style === "free" ? "free" : "choices";
}

/** The shop's own hourly rate (`public_data.hourly_rate`, digits, set in Settings), or "" when it set none. It is what
 *  the rate box starts with when a client picks the hourly choice, never a price they cannot change. */
export function shopRateOf(publicData: unknown): string {
  return `${(publicData as { hourly_rate?: unknown } | null | undefined)?.hourly_rate ?? ""}`.trim();
}

/** An amount as somebody typed it, with a "$" in front when it starts with a digit: "450" and "$450" are "$450",
 *  "1,200.00" is "$1,200.00". A word stays a word ("TBD"): the box is optional, and what a client typed is never
 *  dropped on the floor, because a price that silently vanished from a signed waiver is worse than an odd one. */
export function money(value: unknown): string {
  const text = `${value ?? ""}`.trim().replace(/\s+/g, " ").replace(/^\$\s*/, "");
  if (!text) return "";
  return /^\d/.test(text) ? `$${text}` : text;
}

/** The boxes as they stand once the client picks the hourly choice: the shop's own rate filled into the rate box, for
 *  them to keep or change. A rate already in the box stays, so switching to another choice and back never undoes a
 *  number somebody typed. */
export function prefillRate(parts: PriceParts, shopRate: string): PriceParts {
  const rate = shopRate.trim();
  return parts.rate.trim() || !rate ? parts : { ...parts, rate };
}

/** The one string the form posts as `price`, or "" when nothing was chosen or no number given (an unanswered box
 *  prints nothing on the signed waiver, never "(None)"). The hourly rate is whatever is in the rate box. */
export function composePrice(kind: PriceKind, parts: PriceParts, words: PriceWords): string {
  const tag = (label: string) => ` (${label.toLowerCase()})`;
  if (kind === "fixed") {
    const amount = money(parts.amount);
    return amount ? `${amount}${tag(words.priceKindFixed)}` : "";
  }
  if (kind === "range") {
    const low = money(parts.low);
    const high = money(parts.high);
    if (low && high) return `${low} ${words.priceRangeJoin} ${high}${tag(words.priceKindRange)}`;
    const one = low || high;
    return one ? `${one}${tag(words.priceKindRange)}` : "";
  }
  if (kind === "hourly") {
    const rate = money(parts.rate);
    return rate ? `${rate}${words.pricePerHour}${tag(words.priceKindHourly)}` : "";
  }
  return "";
}
